import { Locale } from '@whizz/api-model';
import * as i18next from 'i18next';
import * as luxonPackage from 'luxon';
import React, { createContext, memo, useReducer, useCallback, useEffect, useContext } from 'react';
import * as initReactI18next from 'react-i18next';
import { Trans } from 'react-i18next';

const context = createContext({
    locale: null,
    locales: [],
    setLocale: () => { },
    translate: () => { },
    addResourceBundle: () => { },
});

const defaultLocale = new Locale();
const Provider = context.Provider;
const initialState = {
    i18next: null,
    locale: null,
    locales: [],
};
const reducer = (state, action) => {
    switch (action.type) {
        case 'init':
            return {
                ...state,
                i18next: action.payload.i18next,
                locales: action.payload.locales,
            };
        case 'locale':
            return {
                ...state,
                locale: action.payload,
            };
        default:
            return state;
    }
};
const I18nProvider = ({ children, loadingComponent = () => React.createElement("div", null, "Loading..."), locale = defaultLocale, luxon, translations, }) => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const hasLocale = useCallback((locale) => {
        if (typeof locale !== 'object' && typeof locale !== 'string') {
            throw Error('Unexpected locale type. It should be a Locale instance or an id (string)');
        }
        const id = typeof locale === 'object' ? locale.id : locale;
        return undefined !== state.locales.find((l) => l.id === id);
    }, [state.locales]);
    const setLocale = useCallback((locale) => {
        if (typeof locale !== 'object' && typeof locale !== 'string') {
            throw Error('Unexpected locale type. It should be a Locale instance or an id (string)');
        }
        if (hasLocale(locale) && state.i18next) {
            const id = typeof locale === 'object' ? locale.id : locale;
            state.i18next.changeLanguage(id, () => dispatch({ type: 'locale', payload: new Locale({ id: id }) }));
        }
    }, [hasLocale, state.i18next]);
    useEffect(() => {
        // Create a new instance instead of using the singleton exposed by i18next. This allows child
        // components to use a different locale than its parent (e.g. @whizz/react-lesson-player may
        // display lessons using Student.locale whereas @whizz/ui-student will use
        // Student.locale_preferred).
        const actualI18next = i18next.default.createInstance();
        const i18NextOptions = {
            fallbackLng: translations ? [defaultLocale.id].concat(Object.keys(translations)) : defaultLocale.id,
            interpolation: {
                escapeValue: false,
                format: (value, format = luxonPackage.DateTime.DATETIME_FULL, lng) => {
                    var _a;
                    if (luxon && value instanceof luxonPackage.DateTime) {
                        if (typeof format === 'string') {
                            return value.toLocaleString(luxon.DateTime[(_a = format) !== null && _a !== void 0 ? _a : 'DATETIME_FULL']);
                        }
                        return value.toLocaleString(format);
                    }
                    return value;
                },
            },
            lowerCaseLng: true,
        };
        if (translations) {
            const resources = Object.entries(translations).reduce((acc, [key, resource]) => Object.assign(acc, { [key]: { translation: resource } }), {});
            Object.assign(i18NextOptions, { resources: resources });
        }
        actualI18next
            .use(initReactI18next.initReactI18next)
            .init(i18NextOptions, () => {
            dispatch({
                type: 'init',
                payload: {
                    i18next: actualI18next,
                    locales: Object.entries(translations).map(([id, translation]) => {
                        const defaultNS = actualI18next.options.defaultNS;
                        if (defaultNS === null || defaultNS === undefined) {
                            throw new Error('Unexpected defaultNS type, it should be an array!');
                        }
                        actualI18next.addResourceBundle(id, defaultNS[0], translation);
                        return new Locale({ id });
                    }),
                },
            });
        });
    }, [luxon, translations]);
    const addResourceBundle = (lng, resources, deep, overwrite) => {
        const actualI18next = state.i18next;
        if (!actualI18next) {
            throw new Error('No i18next instance');
        }
        const defaultNS = actualI18next.options.defaultNS;
        if (!defaultNS) {
            throw new Error('Unexpected defaultNS type, it should be an array!');
        }
        return actualI18next.addResourceBundle(lng, defaultNS[0], resources, deep, overwrite);
    };
    useEffect(() => {
        setLocale(locale);
    }, [locale, setLocale]);
    if (!state.locale) {
        return loadingComponent;
    }
    window.document.documentElement.lang = `${state.locale.language}-${state.locale.country.toUpperCase()}`;
    if (luxon) {
        luxon.Settings.defaultLocale = state.locale.id;
    }
    const actualI18next = state.i18next;
    if (actualI18next === null || actualI18next === undefined) {
        throw new Error('Unexpected i18next type, it should be a valid i18n object!');
    }
    return (React.createElement(Provider, { value: {
            setLocale,
            locale: state.locale,
            locales: state.locales,
            translate: actualI18next.t.bind(state.i18next),
            addResourceBundle,
        } }, children));
};
var I18nProvider$1 = memo(I18nProvider);

const useI18n = () => useContext(context);

const Consumer = context.Consumer;
function withI18n(Component) {
    return (props) => {
        return (React.createElement(Consumer, null, (value) => React.createElement(Component, { ...props, ...value })));
    };
}

const Translate = ({ children, i18nKey, values = {}, }) => (React.createElement(Trans, { components: children, i18nKey: i18nKey, values: values }));
var Translate$1 = memo(Translate);

export { I18nProvider$1 as I18nProvider, Translate$1 as Translate, useI18n, withI18n };
